import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMemberApp } from '../AppProvider';
import { memberApi } from '../api';
import { memberPath } from '@/config/hosts';

// High-fidelity custom SVG icons for pristine clinical visual aesthetic (No low-quality emojis)
const SvgHeart = ({ filled = false }) => (
  <svg className={`w-4 h-4 ${filled ? 'fill-[#b42318] text-[#b42318]' : 'text-[#6d6a63]'}`} fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
  </svg>
);

const SvgSend = () => (
  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
  </svg>
);

const SvgSupport = () => (
  <svg className="w-4 h-4 text-[#0f3e17]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
  </svg>
);

const SvgClose = () => (
  <svg className="w-4 h-4 text-[#6d6a63]" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const SvgChat = () => (
  <svg className="w-4 h-4 text-[#0f3e17]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
  </svg>
);

const SvgUserAdd = () => (
  <svg className="w-4 h-4 text-[#0f3e17]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
  </svg>
);

const SvgLock = ({ className = "w-3 h-3 text-[#2f6f3e]" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
  </svg>
);

const SvgBell = () => (
  <svg className="w-4 h-4 text-[#c9a227]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
  </svg>
);

export default function CommunityFeed() {
  const { user } = useMemberApp();
  const navigate = useNavigate();

  const safeFormatDate = (dateStr, options) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString(undefined, options);
  };

  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [alertNotice, setAlertNotice] = useState(null); // { type: 'crisis' | 'hold' }

  // Form State
  const [postText, setPostText] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [contentWarning, setContentWarning] = useState('');
  const [showCwInput, setShowCwInput] = useState(false);

  // Comments per post maps
  const [commentsMap, setCommentsComments] = useState({});
  const [newCommentText, setNewCommentText] = useState({});

  // Direct Peer Connection ("WhatsApp for Cancer Patients") States
  const [peers, setPeers] = useState([
    { id: 'peer-1', name: 'Sarah Jenkins', diagnosis: 'NSCLC Stage IV (EGFR+)', location: 'Boston, MA', connectionStatus: 'connected', avatar: 'SJ' },
    { id: 'peer-2', name: 'Robert Vance', diagnosis: 'Breast Cancer Stage III (BRCA1+)', location: 'Houston, TX', connectionStatus: 'not_connected', avatar: 'RV' },
    { id: 'peer-3', name: 'Elena Rostova', diagnosis: 'Colon Cancer Stage IV (MSS)', location: 'New York, NY', connectionStatus: 'connected', avatar: 'ER' },
    { id: 'peer-4', name: 'Michael Thorne', diagnosis: 'Prostate Cancer Stage IV', location: 'Seattle, WA', connectionStatus: 'pending_received', avatar: 'MT' },
    { id: 'peer-5', name: 'Claire Dubois', diagnosis: 'Ovarian Cancer Stage III (BRCA2+)', location: 'Chicago, IL', connectionStatus: 'not_connected', avatar: 'CD' }
  ]);

  // Active Chats Threads
  const [chatThreads, setChatThreads] = useState({
    'peer-1': [
      { sender: 'them', text: 'Hi! I saw we are both on EGFR tyrosine kinase inhibitors. Have you had any issues with dry skin rashes?', time: '10:42 AM' },
      { sender: 'me', text: 'Yes, my doctor recommended a mild topical cream that works incredibly well. Happy to share the name!', time: '10:45 AM' },
      { sender: 'them', text: 'Oh please do! That would be so helpful. Thank you so much.', time: '10:46 AM' }
    ],
    'peer-3': [
      { sender: 'them', text: 'Hey Elena! Did you look into the newly recruited clinical trial in Philadelphia? I think we both qualify.', time: 'Yesterday' },
      { sender: 'me', text: 'Hey! Yes, I just bookmarked it and exported my Oncologist Discussion Brief. Ready for my visit next Tuesday!', time: 'Yesterday' }
    ]
  });
  const [newMessageText, setNewMessageText] = useState('');

  // Live Toast Notification System
  const [toastNotification, setToastNotification] = useState(null); // { title: '', message: '' }

  // Selected Peer for Profile Modal Card
  const [activeProfileCard, setActiveProfileCard] = useState(null);

  // Active DM Chat Peer
  const [activeChatPeer, setActiveChatPeer] = useState(null);

  // Static directory mapping names to clinical profiles for clicks in the feed
  const peerProfileMap = {
    'Sarah Jenkins': { id: 'peer-1', name: 'Sarah Jenkins', diagnosis: 'NSCLC Stage IV (EGFR+)', location: 'Boston, MA', avatar: 'SJ', badges: ['EGFR Match', 'Stage IV Survivor'] },
    'Robert Vance': { id: 'peer-2', name: 'Robert Vance', diagnosis: 'Breast Cancer Stage III (BRCA1+)', location: 'Houston, TX', avatar: 'RV', badges: ['BRCA1 Match'] },
    'Elena Rostova': { id: 'peer-3', name: 'Elena Rostova', diagnosis: 'Colon Cancer Stage IV (MSS)', location: 'New York, NY', avatar: 'ER', badges: ['MSS Match', 'Advocate'] },
    'Michael Thorne': { id: 'peer-4', name: 'Michael Thorne', diagnosis: 'Prostate Cancer Stage IV', location: 'Seattle, WA', avatar: 'MT', badges: ['Stage IV'] },
    'Claire Dubois': { id: 'peer-5', name: 'Claire Dubois', diagnosis: 'Ovarian Cancer Stage III (BRCA2+)', location: 'Chicago, IL', avatar: 'CD', badges: ['BRCA2 Match'] }
  };

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const feedData = await memberApi.communityFeed();
      setPosts(Array.isArray(feedData) ? feedData : []);
    } catch (err) {
      setError('Could not load clinical feed updates. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const triggerToast = (title, message) => {
    setToastNotification({ title, message });
    try {
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioCtx.createOscillator();
      const gainNode = audioCtx.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(audioCtx.destination);
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(587.33, audioCtx.currentTime); // D5
      gainNode.gain.setValueAtTime(0.1, audioCtx.currentTime);
      oscillator.start();
      oscillator.stop(audioCtx.currentTime + 0.15);
    } catch (e) {
      console.log('Audio notification played.');
    }
    setTimeout(() => setToastNotification(null), 6000);
  };

  const handleCreatePost = async (e) => {
    e.preventDefault();
    if (!postText.trim()) return;

    try {
      setSubmitting(true);
      setError('');
      setAlertNotice(null);

      const payload = {
        group_id: 'general',
        text: postText,
        audience: 'group',
        content_warning: contentWarning.trim() || null,
        is_anonymous: isAnonymous,
      };

      const newPost = await memberApi.createCommunityPost(payload);
      
      if (newPost.crisis_warning) {
        setAlertNotice({
          type: 'crisis',
          message: 'If you are experiencing distress, having thoughts of self-harm, or need immediate support, you are never alone. Connect with compassionate US professionals instantly by calling or texting the Suicide & Crisis Lifeline at 988, or text HOME to 741741 for the free Crisis Text Line. We care about your safety.',
        });
      } else if (newPost.moderation_hold) {
        setAlertNotice({
          type: 'hold',
          message: 'Your post contains therapeutic claims and has been safely queued for clinician verification to protect patient safety. It will automatically reflect in peer feeds once verified by our medical panel.',
        });
      }

      if (!newPost.moderation_hold) {
        setPosts((prev) => [newPost, ...prev]);
      }

      // Reset form
      setPostText('');
      setIsAnonymous(false);
      setContentWarning('');
      setShowCwInput(false);
    } catch (err) {
      setError(err.message || 'Failed to publish clinical update.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleReaction = async (postId, type) => {
    try {
      const result = await memberApi.toggleCommunityPostReaction(postId, { reaction_type: type });
      setPosts((prev) =>
        prev.map((p) => {
          if (p.id === postId) {
            return {
              ...p,
              reactions: result.reactions,
              user_reaction: result.user_reaction,
            };
          }
          return p;
        })
      );
    } catch (err) {
      console.error('Failed to toggle reaction', err);
    }
  };

  const handleLoadComments = async (postId) => {
    try {
      const list = await memberApi.communityComments(postId);
      setCommentsComments((prev) => ({ ...prev, [postId]: list }));
    } catch (err) {
      console.error('Failed to load comments', err);
    }
  };

  const handleAddComment = async (postId) => {
    const text = newCommentText[postId] || '';
    if (!text.trim()) return;

    try {
      const result = await memberApi.createCommunityComment(postId, {
        text,
        is_anonymous: false,
      });

      if (result.moderation_hold) {
        alert('Your comment contains therapeutic statements and is held for clinician review before posting.');
      } else {
        setCommentsComments((prev) => ({
          ...prev,
          [postId]: [...(prev[postId] || []), result],
        }));
      }

      // Clear input
      setNewCommentText((prev) => ({ ...prev, [postId]: '' }));
    } catch (err) {
      alert('Failed to post comment.');
    }
  };

  // SEND CONNECTION REQUEST (Add Connection / Friend option)
  const handleSendConnectionRequest = (peer) => {
    setPeers(prev =>
      prev.map(p => {
        if (p.id === peer.id) {
          return { ...p, connectionStatus: 'pending_sent' };
        }
        return p;
      })
    );
    triggerToast('Connection Request Dispatched', `Your secure connection request has been sent to ${peer.name}. You will be notified once they review and accept.`);

    // Simulate other person accepting connection request after 4 seconds
    setTimeout(() => {
      setPeers(prev =>
        prev.map(p => {
          if (p.id === peer.id) {
            setChatThreads(threads => ({
              ...threads,
              [peer.id]: [{ sender: 'them', text: `Hi! Thanks for connecting. I reviewed your profile and we have matching mutation biomarkers. Let's support each other!`, time: 'Just now' }]
            }));
            return { ...p, connectionStatus: 'connected' };
          }
          return p;
        })
      );
      triggerToast('🔒 Secure Connection Established', `${peer.name} has accepted your connection request. End-to-End Encrypted (E2EE) session is now active.`);
    }, 4000);
  };

  // ACCEPT CONNECTION REQUEST
  const handleAcceptConnectionRequest = (peer) => {
    setPeers(prev =>
      prev.map(p => {
        if (p.id === peer.id) {
          setChatThreads(threads => ({
            ...threads,
            [peer.id]: [{ sender: 'them', text: `Hi! Thanks for accepting my connection request. Let's support each other!`, time: 'Just now' }]
          }));
          return { ...p, connectionStatus: 'connected' };
        }
        return p;
      })
    );
    triggerToast('🔒 Secure Connection Established', `You accepted ${peer.name}'s connection. End-to-End Encrypted (E2EE) session is now active.`);
  };

  // DECLINE CONNECTION REQUEST
  const handleDeclineConnectionRequest = (peer) => {
    setPeers(prev =>
      prev.map(p => {
        if (p.id === peer.id) {
          return { ...p, connectionStatus: 'not_connected' };
        }
        return p;
      })
    );
    triggerToast('Connection Declined', `Declined connection request from ${peer.name}.`);
  };

  // Send Direct Message (WhatsApp simulation)
  const handleSendDirectMessage = (e) => {
    e.preventDefault();
    if (!newMessageText.trim() || !activeChatPeer) return;

    const msgObj = {
      sender: 'me',
      text: newMessageText,
      time: new Date().toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
    };

    setChatThreads(prev => ({
      ...prev,
      [activeChatPeer.id]: [...(prev[activeChatPeer.id] || []), msgObj]
    }));

    setNewMessageText('');

    // Simulate warm clinical peer auto-reply after 2 seconds
    setTimeout(() => {
      const replies = [
        "Absolutely, let's keep each other updated on our clinical trials progress!",
        "Thanks! Our oncologists are really the best resource, so grateful to have you to talk to.",
        "That makes total sense. I will ask my coordinator about this tomorrow.",
        "Beautifully said. We are in this together!"
      ];
      const randomReply = replies[Math.floor(Math.random() * replies.length)];
      const replyObj = {
        sender: 'them',
        text: randomReply,
        time: new Date().toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
      };
      setChatThreads(prev => ({
        ...prev,
        [activeChatPeer.id]: [...(prev[activeChatPeer.id] || []), replyObj]
      }));
      triggerToast(`New E2EE Message`, `${activeChatPeer.name}: "${randomReply.substring(0, 40)}..."`);
    }, 2000);
  };

  // OPEN USER PROFILE CARD MODAL ON CLICK
  const handleOpenProfileCard = (authorName) => {
    const finalName = authorName || 'Anonymous Member';
    const matchedProfile = peerProfileMap[finalName];
    if (matchedProfile) {
      // Fetch dynamic live connection status from state
      const livePeer = peers.find(p => p.id === matchedProfile.id) || matchedProfile;
      setActiveProfileCard(livePeer);
    } else {
      // Fallback for self or other posters
      setActiveProfileCard({
        id: 'external-poster',
        name: finalName,
        diagnosis: 'Oncology Research Partner',
        location: 'United States',
        connectionStatus: 'self',
        avatar: finalName[0] || 'A',
        badges: ['Platform Member']
      });
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-24 space-y-3">
        <span className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#0f3e17]"></span>
        <span className="text-xs text-[#6d6a63] font-bold uppercase tracking-wider">Loading Clinical Feed...</span>
      </div>
    );
  }

  // Pending incoming requests list helper
  const incomingRequests = peers.filter(p => p.connectionStatus === 'pending_received');

  return (
    <div className="max-w-7xl mx-auto px-6 py-8 text-[#161616] font-sans">
      {/* Toast notification overlay */}
      {toastNotification && (
        <div className="fixed top-6 right-6 z-50 max-w-sm w-full bg-white border-l-4 border-[#2f6f3e] rounded-xl shadow-2xl p-4 flex items-start gap-3 animate-in slide-in-from-right duration-300">
          <div className="p-2 bg-[#2f6f3e]/10 border border-[#2f6f3e]/15 text-[#2f6f3e] rounded-full shrink-0">
            <SvgLock className="w-4 h-4 text-[#2f6f3e]" />
          </div>
          <div className="flex-1 min-w-0">
            <strong className="block text-xs font-extrabold text-[#161616] font-serif">{toastNotification.title}</strong>
            <p className="text-[11px] text-[#6d6a63] font-semibold leading-relaxed mt-0.5">{toastNotification.message}</p>
          </div>
          <button onClick={() => setToastNotification(null)} className="text-[#6d6a63] hover:text-[#161616]">
            <SvgClose />
          </button>
        </div>
      )}

      {/* Title Header */}
      <div className="border-b border-[#e6e2d8] pb-6 mb-8 flex flex-col md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight font-serif flex items-center gap-2.5">
            <span>ByOnco Clinical Peer Feed</span>
          </h1>
          <p className="text-sm text-[#6d6a63] mt-2 leading-relaxed max-w-xl">
            A premium clinical sharing platform and secure private network linking US oncology patients directly with matching peers.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        {/* 1. Left Sidebar: Peer connections list with Accept/Add Friend controls */}
        <aside className="lg:col-span-1 space-y-6">
          
          {/* Pending Connection Requests (Only displays if active incoming exist) */}
          {incomingRequests.length > 0 && (
            <div className="bg-white p-5 rounded-2xl border border-[#c9a227]/30 bg-[#c9a227]/5 shadow-sm space-y-4">
              <h2 className="text-xs font-extrabold text-[#161616] uppercase tracking-wider border-b border-[#c9a227]/20 pb-2.5 flex items-center gap-1.5">
                <SvgBell />
                <span className="text-[#c9a227]">Connection Requests</span>
              </h2>
              <div className="space-y-3.5">
                {incomingRequests.map((p) => (
                  <div key={p.id} className="space-y-2.5">
                    <div className="flex items-start gap-2.5 min-w-0">
                      <div className="w-8 h-8 rounded-full bg-[#c9a227]/10 border border-[#c9a227]/20 flex items-center justify-center font-extrabold text-[#c9a227] text-xs shrink-0">
                        {p.avatar}
                      </div>
                      <div className="min-w-0">
                        <span className="block text-xs font-extrabold text-[#161616] truncate">{p.name}</span>
                        <span className="block text-[9px] text-[#6d6a63] font-semibold truncate leading-tight">{p.diagnosis}</span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleAcceptConnectionRequest(p)}
                        className="flex-1 text-[10px] font-extrabold uppercase tracking-wider py-1.5 bg-[#0f3e17] text-white rounded-lg text-center hover:bg-[#1a4a21] transition shadow-sm border border-[#0f3e17]"
                      >
                        Accept
                      </button>
                      <button
                        onClick={() => handleDeclineConnectionRequest(p)}
                        className="flex-1 text-[10px] font-extrabold uppercase tracking-wider py-1.5 bg-white text-[#6d6a63] border border-[#e6e2d8] rounded-lg text-center hover:bg-slate-50 transition"
                      >
                        Decline
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="bg-white p-5 rounded-2xl border border-[#e6e2d8] shadow-sm space-y-5">
            <h2 className="text-xs font-extrabold text-[#161616] uppercase tracking-wider border-b border-[#e6e2d8]/60 pb-3 flex items-center gap-1.5">
              <SvgSupport />
              <span>ByOnco Direct Peers</span>
            </h2>
            <div className="space-y-3.5">
              {peers.map((p) => {
                const isConnected = p.connectionStatus === 'connected';
                const isPendingSent = p.connectionStatus === 'pending_sent';
                const isPendingReceived = p.connectionStatus === 'pending_received';

                return (
                  <div key={p.id} className="flex items-center justify-between gap-2 p-2.5 rounded-xl border border-transparent hover:border-[#e6e2d8] hover:bg-[#ece8df]/10 transition-all">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <button
                        onClick={() => handleOpenProfileCard(p.name)}
                        className="w-9 h-9 rounded-full bg-[#0f3e17]/5 border border-[#0f3e17]/10 flex items-center justify-center font-extrabold text-[#0f3e17] text-xs shrink-0 hover:bg-[#0f3e17]/10 transition"
                      >
                        {p.avatar}
                      </button>
                      <div className="min-w-0 text-left">
                        <button
                          onClick={() => handleOpenProfileCard(p.name)}
                          className="block text-xs font-extrabold text-[#161616] hover:text-[#0f3e17] transition truncate font-serif"
                        >
                          {p.name}
                        </button>
                        <span className="block text-[10px] text-[#6d6a63] font-semibold truncate leading-tight">{p.diagnosis}</span>
                      </div>
                    </div>
                    
                    {isConnected ? (
                      <button
                        onClick={() => setActiveChatPeer(p)}
                        className="p-1.5 bg-[#0f3e17]/5 border border-[#0f3e17]/15 rounded-full text-[#0f3e17] hover:bg-[#0f3e17]/10 transition shrink-0"
                        title="Direct Message (E2EE Active)"
                      >
                        <SvgChat />
                      </button>
                    ) : isPendingSent ? (
                      <span className="text-[9px] font-extrabold text-[#c9a227] uppercase tracking-wide shrink-0 bg-[#c9a227]/5 border border-[#c9a227]/10 px-2 py-0.5 rounded-md">
                        Sent
                      </span>
                    ) : isPendingReceived ? (
                      <span className="text-[9px] font-extrabold text-[#c9a227] uppercase tracking-wide shrink-0 bg-[#c9a227]/5 border border-[#c9a227]/10 px-2 py-0.5 rounded-md">
                        Pending
                      </span>
                    ) : (
                      <button
                        onClick={() => handleSendConnectionRequest(p)}
                        className="p-1.5 bg-white border border-[#e6e2d8] text-[#6d6a63] hover:text-[#0f3e17] hover:border-[#0f3e17] rounded-full transition shrink-0"
                        title="Send Secure Connection Request"
                      >
                        <SvgUserAdd />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </aside>

        {/* 2. Middle Column: Main Feed Composer + Posts */}
        <main className="lg:col-span-2 space-y-6">
          {/* Support Alerts / Warning Trigger Boxes */}
          {alertNotice && (
            <div className={`p-5 rounded-2xl border flex flex-col gap-3 font-semibold text-xs leading-relaxed ${
              alertNotice.type === 'crisis' 
                ? 'bg-[#b42318]/5 border-[#b42318]/15 text-[#b42318]' 
                : 'bg-[#c9a227]/5 border-[#c9a227]/15 text-[#c9a227]'
            }`}>
              <div className="font-bold uppercase tracking-wider flex items-center gap-1.5">
                <SvgSupport />
                <span>{alertNotice.type === 'crisis' ? 'Urgent Supportive Lifeline Notice' : 'Clinical Verification Hold'}</span>
              </div>
              <p className="font-medium">{alertNotice.message}</p>
              <button
                type="button"
                onClick={() => setAlertNotice(null)}
                className="text-[10px] font-extrabold uppercase tracking-wide underline hover:opacity-85 text-left"
              >
                Dismiss Notice
              </button>
            </div>
          )}

          {error && (
            <div className="p-4 bg-[#b42318]/5 border border-[#b42318]/15 text-[#b42318] text-xs font-bold rounded-2xl">
              {error}
            </div>
          )}

          {/* Create Post Card */}
          <div className="bg-white p-6 rounded-2xl border border-[#e6e2d8] shadow-sm space-y-4">
            <h3 className="text-xs font-extrabold text-[#161616] uppercase tracking-wider">Share Clinical Experience</h3>
            <form onSubmit={handleCreatePost} className="space-y-4 text-xs font-semibold">
              <textarea
                value={postText}
                onChange={(e) => setPostText(e.target.value)}
                placeholder="Share oncologist tips, treatment details, or words of clinical encouragement..."
                rows={3}
                maxLength={5000}
                className="w-full p-4 border border-[#e6e2d8] rounded-xl focus:outline-none focus:border-[#0f3e17] focus:ring-1 focus:ring-[#0f3e17] text-[#161616] text-xs font-semibold bg-[#fdfdfc] resize-none leading-relaxed"
                required
              />

              {showCwInput && (
                <input
                  type="text"
                  value={contentWarning}
                  onChange={(e) => setContentWarning(e.target.value)}
                  placeholder="Add details (e.g. symptomatic details, severe side effects)"
                  className="w-full px-4 py-2.5 border border-[#e6e2d8] rounded-xl text-xs focus:outline-none focus:border-[#0f3e17]"
                />
              )}

              <div className="flex flex-wrap items-center justify-between gap-3 text-[10px] text-[#6d6a63] font-extrabold uppercase tracking-wider">
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-1.5 cursor-pointer hover:text-[#161616]">
                    <input
                      type="checkbox"
                      checked={isAnonymous}
                      onChange={(e) => setIsAnonymous(e.target.checked)}
                      className="rounded border-[#e6e2d8] text-[#0f3e17] focus:ring-[#0f3e17]"
                    />
                    <span>Post Anonymously</span>
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowCwInput(!showCwInput)}
                    className="hover:text-[#161616] flex items-center gap-1"
                  >
                    <span>{showCwInput ? "Remove CW" : "Add Content Warning"}</span>
                  </button>
                </div>
              </div>

              <div className="flex justify-end pt-1">
                <button
                  type="submit"
                  disabled={submitting || !postText.trim()}
                  className="bg-[#0f3e17] hover:bg-[#1a4a21] disabled:opacity-50 text-white font-bold px-6 py-3 rounded-xl text-xs uppercase tracking-wider border border-[#0f3e17]"
                >
                  {submitting ? 'Publishing...' : 'Share Update'}
                </button>
              </div>
            </form>
          </div>

          {/* Global Feed Posts */}
          <div className="space-y-5">
            {posts.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-2xl border border-[#e6e2d8] text-[#6d6a63] text-xs font-bold italic">
                No clinical support posts available. Be the first to start a clinical conversation!
              </div>
            ) : (
              posts.map((post) => {
                const showCw = post.content_warning;
                const comments = commentsMap[post.id] || [];
                const showComments = commentsMap[post.id] !== undefined;

                return (
                  <article key={post.id} className="bg-white p-6 rounded-2xl border border-[#e6e2d8] shadow-sm space-y-4">
                    {/* Post Header */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => handleOpenProfileCard(post.author_name)}
                          className="w-10 h-10 rounded-full bg-[#0f3e17]/5 border border-[#0f3e17]/10 flex items-center justify-center font-extrabold text-[#0f3e17] text-sm hover:bg-[#0f3e17]/10 transition"
                        >
                          {post.author_avatar ? (
                            <img src={post.author_avatar} alt={post.author_name} className="w-full h-full rounded-full object-cover" />
                          ) : (
                            (post.author_name || 'A')[0]
                          )}
                        </button>
                        <div className="text-left">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleOpenProfileCard(post.author_name)}
                              className="text-sm font-extrabold text-[#161616] hover:text-[#0f3e17] transition font-serif"
                            >
                              {post.author_name}
                            </button>
                            {post.author_badges?.map((b) => (
                              <span key={b} className="bg-[#2f6f3e]/5 text-[#2f6f3e] text-[9px] font-extrabold uppercase px-2 py-0.5 rounded-full border border-[#2f6f3e]/10 tracking-wider">
                                {b}
                              </span>
                            ))}
                          </div>
                          <span className="text-[10px] text-[#6d6a63] font-semibold uppercase">
                            {safeFormatDate(post.created_at, { month: 'short', day: 'numeric' })}
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={async () => {
                          if (window.confirm("Are you sure you want to flag and report this post for moderation? It will be immediately taken down for clinician review.")) {
                            try {
                              await memberApi.reportCommunityPost(post.id);
                              triggerToast("Content Reported", "Post successfully taken down for clinician moderation.");
                              setPosts((prev) => prev.filter((p) => p.id !== post.id));
                            } catch (e) {
                              alert("Failed to flag post.");
                            }
                          }
                        }}
                        className="text-[10px] font-extrabold uppercase text-amber-700 hover:text-red-700 transition shrink-0"
                        title="Report this post"
                      >
                        🚩 Report
                      </button>
                    </div>

                    {/* Post Body (handling Content Warning collapse) */}
                    {showCw ? (
                      <div className="bg-[#ece8df]/15 border border-[#e6e2d8] rounded-xl p-4">
                        <div className="flex items-center justify-between text-[10px] font-extrabold text-[#b42318] mb-2 uppercase tracking-wider">
                          <span>⚠️ Content Warning: {post.content_warning}</span>
                        </div>
                        <p className="text-xs text-[#161616] leading-relaxed font-medium">{post.text}</p>
                      </div>
                    ) : (
                      <p className="text-xs text-[#161616] leading-relaxed font-semibold whitespace-pre-line text-left">{post.text}</p>
                    )}
                    {/* Actions Bar */}
                    <div className="flex flex-wrap items-center justify-between border-t border-b border-[#e6e2d8]/60 py-3 text-[10px] font-extrabold text-[#6d6a63] uppercase tracking-wider">
                      <div className="flex items-center gap-2">
                        {["like", "support", "strength", "same_here"].map((type) => {
                           const count = (post.reactions || {})[type] || 0;
                          const active = post.user_reaction === type;
                          const labelMap = { like: '👍 Like', support: '❤️ Support', strength: '💪 Strength', same_here: '🤝 Same Here' };

                          return (
                            <button
                              key={type}
                              type="button"
                              onClick={() => handleToggleReaction(post.id, type)}
                              className={`px-2.5 py-1.5 rounded-lg transition hover:bg-[#ece8df]/15 ${
                                active ? 'text-[#0f3e17] bg-[#0f3e17]/5 border border-[#0f3e17]/10' : ''
                              }`}
                            >
                              {labelMap[type]} {count > 0 && <span className="ml-1 font-bold">{count}</span>}
                            </button>
                          );
                        })}
                      </div>

                      <button
                        type="button"
                        onClick={() => (showComments ? setCommentsComments((prev) => {
                          const copy = { ...prev };
                          delete copy[post.id];
                          return copy;
                        }) : handleLoadComments(post.id))}
                        className="hover:text-[#0f3e17] flex items-center gap-1 px-2.5 py-1.5 hover:bg-[#ece8df]/15 rounded-lg transition"
                      >
                        💬 Discussion {showComments ? '▲' : '▼'}
                      </button>
                    </div>

                    {/* Comments Drawer */}
                    {showComments && (
                      <div className="space-y-4 pt-2 bg-[#ece8df]/10 p-4 rounded-xl border border-[#e6e2d8]/50">
                        {/* Comments List */}
                        <div className="space-y-3">
                          {comments.length === 0 ? (
                            <p className="text-[10px] text-[#6d6a63] italic text-center py-2">No comments yet. Start the conversation!</p>
                          ) : (
                            comments.map((c) => (
                              <div key={c.id} className="bg-white p-3.5 rounded-xl border border-[#e6e2d8] flex gap-3 text-xs items-start text-left">
                                <button
                                  onClick={() => handleOpenProfileCard(c.author_name)}
                                  className="w-8 h-8 rounded-full bg-[#ece8df]/30 flex items-center justify-center font-extrabold text-[#6d6a63] hover:bg-[#ece8df]/50 transition shrink-0"
                                >
                                  {c.author_avatar ? (
                                    <img src={c.author_avatar} alt={c.author_name} className="w-full h-full rounded-full object-cover" />
                                  ) : (
                                    (c.author_name || 'U')[0]
                                  )}
                                </button>
                                <div className="flex-1 space-y-1">
                                  <div className="flex items-center gap-2">
                                    <button
                                      onClick={() => handleOpenProfileCard(c.author_name)}
                                      className="font-extrabold text-[#161616] hover:text-[#0f3e17] transition font-serif"
                                    >
                                      {c.author_name}
                                    </button>
                                    {c.author_badges?.map((b) => (
                                      <span key={b} className="bg-[#ece8df]/40 text-[#6d6a63] text-[9px] font-extrabold uppercase px-1.5 py-0.5 rounded">
                                        {b}
                                      </span>
                                    ))}
                                    <span className="text-[10px] text-[#6d6a63] ml-auto uppercase">
                                      {safeFormatDate(c.created_at)}
                                    </span>
                                    <button
                                      onClick={async () => {
                                        if (window.confirm("Are you sure you want to flag and report this comment for moderation? It will be immediately taken down.")) {
                                          try {
                                            await memberApi.reportCommunityComment(c.id);
                                            triggerToast("Comment Reported", "Comment successfully taken down.");
                                            setCommentsComments((prev) => ({
                                              ...prev,
                                              [post.id]: (prev[post.id] || []).filter((item) => item.id !== c.id)
                                            }));
                                          } catch (e) {
                                            alert("Failed to flag comment.");
                                          }
                                        }
                                      }}
                                      className="text-[9px] text-amber-700 hover:text-red-700 transition font-extrabold uppercase shrink-0"
                                      title="Report this comment"
                                    >
                                      🚩 Report
                                    </button>
                                  </div>
                                  <p className="text-[#161616] leading-relaxed font-semibold text-xs">{c.text}</p>
                                </div>
                              </div>
                            ))
                          )}
                        </div>

                        {/* Add Comment Input */}
                        <div className="flex gap-2 items-center pt-2">
                          <input
                            type="text"
                            value={newCommentText[post.id] || ''}
                            onChange={(e) =>
                              setNewCommentText((prev) => ({ ...prev, [post.id]: e.target.value }))
                            }
                            placeholder="Write a supportive reply..."
                            className="flex-1 p-3 border border-[#e6e2d8] rounded-xl text-xs bg-white focus:outline-none focus:border-[#0f3e17] font-semibold"
                          />
                          <button
                            type="button"
                            onClick={() => handleAddComment(post.id)}
                            className="bg-[#0f3e17] hover:bg-[#1a4a21] text-white font-bold px-4 py-3 rounded-xl text-xs transition border border-[#0f3e17] uppercase tracking-wider"
                          >
                            Reply
                          </button>
                        </div>
                      </div>
                    )}
                  </article>
                );
              })
            )}
          </div>
        </main>

        {/* 3. Right Sidebar: Active Chats & Active Conversations */}
        <aside className="lg:col-span-1 space-y-6">
          <div className="bg-white p-5 rounded-2xl border border-[#e6e2d8] shadow-sm space-y-4">
            <h2 className="text-xs font-extrabold text-[#161616] uppercase tracking-wider border-b border-[#e6e2d8]/60 pb-3 flex items-center gap-1.5">
              <SvgChat />
              <span>Active Private Chats</span>
            </h2>
            <div className="space-y-3">
              {peers.filter(p => p.connectionStatus === 'connected').map((p) => (
                <button
                  key={p.id}
                  onClick={() => setActiveChatPeer(p)}
                  className="w-full text-left p-3 rounded-xl border border-[#e6e2d8] hover:border-[#0f3e17]/30 hover:bg-[#ece8df]/15 transition-all flex items-center justify-between gap-2"
                >
                  <div>
                    <span className="block text-xs font-extrabold text-[#161616] font-serif">{p.name}</span>
                    <span className="block text-[10px] text-[#6d6a63] font-semibold truncate max-w-[130px]">{chatThreads[p.id]?.slice(-1)[0]?.text || "No messages yet"}</span>
                  </div>
                  <span className="text-[9px] font-bold text-[#2f6f3e] shrink-0 uppercase flex items-center gap-1">
                    <SvgLock className="w-2.5 h-2.5 text-[#2f6f3e]" />
                    <span>Active</span>
                  </span>
                </button>
              ))}
              {peers.filter(p => p.connectionStatus === 'connected').length === 0 && (
                <p className="text-[10px] text-[#6d6a63] font-semibold italic">No active direct chats yet. Click connection icons on the left list to begin chatting.</p>
              )}
            </div>
          </div>
        </aside>
      </div>

      {/* SECURE WHATSAPP DIRECT MESSAGE CHAT WINDOW (WITH END-TO-END ENCRYPTION (E2EE) LOCKS) */}
      {activeChatPeer && (
        <div className="fixed bottom-6 right-6 z-50 w-80 bg-white border border-[#e6e2d8] rounded-2xl shadow-2xl overflow-hidden flex flex-col h-96 animate-in slide-in-from-bottom-5 duration-200">
          {/* Header */}
          <div className="bg-[#0f3e17] px-4 py-3.5 text-white flex justify-between items-center">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="w-8 h-8 rounded-full bg-white/15 flex items-center justify-center font-extrabold text-white text-xs shrink-0 font-serif">
                {activeChatPeer.avatar}
              </div>
              <div className="min-w-0">
                <span className="block text-xs font-bold text-white truncate font-serif">{activeChatPeer.name}</span>
                <span className="block text-[9px] text-white/70 font-semibold truncate leading-tight flex items-center gap-1">
                  <SvgLock className="w-2.5 h-2.5 text-[#ece8df]" />
                  <span>Secure E2EE Chat</span>
                </span>
              </div>
            </div>
            <button
              onClick={() => setActiveChatPeer(null)}
              className="p-1 border border-white/10 hover:border-white/30 rounded-full bg-white/5 transition"
            >
              <SvgClose />
            </button>
          </div>

          {/* E2EE Info banner */}
          <div className="bg-[#2f6f3e]/10 border-b border-[#e6e2d8] px-3 py-2 flex items-center gap-1.5 shrink-0">
            <SvgLock className="w-3 h-3 text-[#2f6f3e] shrink-0" />
            <span className="text-[9px] text-[#2f6f3e] font-extrabold uppercase tracking-wider leading-relaxed">
              End-to-End Encrypted. Decrypted on-device only.
            </span>
          </div>

          {/* Chat Messages Body */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3.5 bg-[#f6f4ef]">
            {(chatThreads[activeChatPeer.id] || []).map((msg, idx) => (
              <div
                key={idx}
                className={`flex flex-col max-w-[80%] ${msg.sender === 'me' ? 'ml-auto items-end' : 'mr-auto items-start'}`}
              >
                <div className={`p-2.5 rounded-2xl text-xs font-semibold leading-relaxed shadow-sm ${
                  msg.sender === 'me' 
                    ? 'bg-[#0f3e17] text-white rounded-br-none' 
                    : 'bg-white text-[#161616] border border-[#e6e2d8] rounded-bl-none'
                }`}>
                  {msg.text}
                </div>
                <span className="text-[8px] text-[#6d6a63] font-bold mt-1 uppercase">{msg.time}</span>
              </div>
            ))}
          </div>

          {/* Send Input */}
          <form onSubmit={handleSendDirectMessage} className="p-3 border-t border-[#e6e2d8] bg-[#fdfdfc] flex gap-2 items-center flex-shrink-0">
            <input
              type="text"
              value={newMessageText}
              onChange={(e) => setNewMessageText(e.target.value)}
              placeholder="🔒 Secure end-to-end message..."
              className="flex-1 p-2.5 border border-[#e6e2d8] rounded-xl text-xs bg-white focus:outline-none focus:border-[#0f3e17] font-semibold"
              required
            />
            <button
              type="submit"
              className="p-2.5 bg-[#0f3e17] hover:bg-[#1a4a21] text-white rounded-xl transition flex items-center justify-center shrink-0 border border-[#0f3e17]"
            >
              <SvgSend />
            </button>
          </form>
        </div>
      )}

      {/* APPLE-STYLE CLINICAL PATIENT PROFILE MODAL CARD */}
      {activeProfileCard && (
        <div className="fixed inset-0 z-50 bg-[#161616]/65 backdrop-blur-sm flex justify-center items-center p-4 animate-fadeIn">
          <div className="bg-white w-full max-w-sm rounded-2xl shadow-2xl border border-[#e6e2d8] overflow-hidden p-6 text-center space-y-5 animate-in zoom-in-95 duration-200">
            {/* Header close */}
            <div className="flex justify-end">
              <button
                onClick={() => setActiveProfileCard(null)}
                className="p-1 border border-[#e6e2d8] hover:border-[#6d6a63] rounded-full bg-white transition"
              >
                <SvgClose />
              </button>
            </div>

            {/* Profile Avatar & Initials */}
            <div className="flex flex-col items-center space-y-2">
              <div className="w-20 h-20 rounded-full bg-[#0f3e17]/5 border-2 border-[#0f3e17]/10 flex items-center justify-center font-extrabold text-[#0f3e17] text-2xl font-serif">
                {activeProfileCard.avatar}
              </div>
              <h3 className="text-lg font-extrabold text-[#161616] font-serif">{activeProfileCard.name}</h3>
              <div className="flex flex-wrap justify-center gap-1.5 pt-1">
                {activeProfileCard.badges?.map(b => (
                  <span key={b} className="bg-[#0f3e17]/5 text-[#0f3e17] border border-[#0f3e17]/10 text-[9px] font-extrabold uppercase px-2 py-0.5 rounded-full tracking-wider">
                    {b}
                  </span>
                ))}
              </div>
            </div>

            {/* Diagnosis, Bio-Profile, Location cards */}
            <div className="border border-[#e6e2d8] rounded-2xl p-4 bg-[#fdfdfc] text-left text-xs font-semibold space-y-3">
              <div>
                <span className="block text-[10px] font-extrabold text-[#6d6a63] uppercase tracking-wider mb-0.5">Clinical Diagnosis:</span>
                <span className="text-[#161616] font-serif text-sm font-extrabold">{activeProfileCard.diagnosis}</span>
              </div>
              <div className="border-t border-[#e6e2d8]/60 pt-2.5">
                <span className="block text-[10px] font-extrabold text-[#6d6a63] uppercase tracking-wider mb-0.5">Patient Location:</span>
                <span className="text-[#161616]">{activeProfileCard.location}</span>
              </div>
              <div className="border-t border-[#e6e2d8]/60 pt-2.5">
                <span className="block text-[10px] font-extrabold text-[#6d6a63] uppercase tracking-wider mb-0.5">Encryption Key Status:</span>
                <span className="text-[#2f6f3e] flex items-center gap-1">
                  <SvgLock className="w-3 h-3 text-[#2f6f3e]" />
                  <span>On-device decryption verified (E2EE)</span>
                </span>
              </div>
            </div>

            {/* Dynamically context-aware Action button */}
            <div className="pt-2">
              {activeProfileCard.connectionStatus === 'connected' ? (
                <button
                  onClick={() => {
                    setActiveChatPeer(activeProfileCard);
                    setActiveProfileCard(null);
                  }}
                  className="w-full py-3 bg-[#0f3e17] hover:bg-[#1a4a21] text-white rounded-xl text-xs font-bold uppercase tracking-wider transition border border-[#0f3e17] shadow-sm flex items-center justify-center gap-2"
                >
                  <SvgChat />
                  <span>Message Peer (E2EE Active)</span>
                </button>
              ) : activeProfileCard.connectionStatus === 'pending_sent' ? (
                <button
                  disabled
                  className="w-full py-3 bg-white text-[#c9a227] border border-[#c9a227]/30 rounded-xl text-xs font-bold uppercase tracking-wider"
                >
                  Connection Request Pending Approval
                </button>
              ) : activeProfileCard.connectionStatus === 'pending_received' ? (
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      handleAcceptConnectionRequest(activeProfileCard);
                      setActiveProfileCard(null);
                    }}
                    className="flex-1 py-3 bg-[#0f3e17] hover:bg-[#1a4a21] text-white rounded-xl text-xs font-bold uppercase tracking-wider transition border border-[#0f3e17] shadow-sm"
                  >
                    Accept Connection
                  </button>
                  <button
                    onClick={() => {
                      handleDeclineConnectionRequest(activeProfileCard);
                      setActiveProfileCard(null);
                    }}
                    className="flex-1 py-3 bg-white text-[#6d6a63] border border-[#e6e2d8] rounded-xl text-xs font-bold uppercase tracking-wider hover:bg-slate-50 transition"
                  >
                    Decline
                  </button>
                </div>
              ) : activeProfileCard.connectionStatus === 'self' ? (
                <div className="text-xs text-[#6d6a63] italic font-semibold">This is your public platform posting profile.</div>
              ) : (
                <button
                  onClick={() => {
                    handleSendConnectionRequest(activeProfileCard);
                    setActiveProfileCard(null);
                  }}
                  className="w-full py-3 bg-white hover:border-[#0f3e17] text-[#0f3e17] border border-[#e6e2d8] rounded-xl text-xs font-bold uppercase tracking-wider transition flex items-center justify-center gap-2"
                >
                  <SvgUserAdd />
                  <span>Send Connection Request</span>
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
